import React from 'react';

import Radium from '@flipdish/radium';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import max from 'lodash/max';
import { hashHistory } from 'react-router';
import tinycolor from 'tinycolor2';

import * as Api from '../../Api';
import { MenuItemOptionSetItem } from '../../Api';
import * as B from '../../BasketControl';
import { ToCurrency } from '../../BasketControl';
import { Async, Sorted } from '../../Data';
import * as Routes from '../../Routes';
import * as Store from '../../Store';
import { Utilities } from '../../Utilities';
import LoadingPanel from '../LoadingPanel';
import OptionSet from '../OptionSet/index.weborder';
import Price from '../Price';

interface Props {
  readonly?: boolean;
  params: {
    vrid?: number;
    type?: 'collection' | 'delivery';
    id?: number;
    concessionStoreId?: number;
    item: number;
    index: string;
  };
  Item: Async<Api.MenuItem>;
  Restaurant?: Routes.RouteRestaurant;
  OptionsState: {
    Options: Api.MenuItemOptionSetItem[];
    PrevScreenOptions: Api.MenuItemOptionSetItem[][];
    Current: number;
    PrevIndices: number[];
  };
  translate: any;
  Languages: Store.Language[];
  AppDesignScheme: Api.AppDesignScheme;
  isDark: boolean;
  SetLiveOptions: (options: Api.MenuItemOptionSetItem[]) => any;
  handleAddItem: (item: Api.MenuItem) => () => any;
  SetOptionsState: (
    options: Api.MenuItemOptionSetItem[],
    prevScreenOptions: Api.MenuItemOptionSetItem[][],
    current: number,
    prevIndices: number[],
  ) => any;
}

interface State {}

class ConfigureItem extends React.Component<Props, State> {
  constructor(props: Props, context: any) {
    super(props, context);

    (this as any).optionsTop = React.createRef();
  }

  public shouldComponentUpdate(nextProps: Props) {
    if (!nextProps.OptionsState) {
      return false;
    }

    //To prevent flicker
    if (
      this.props.OptionsState.PrevScreenOptions.length <
      nextProps.OptionsState.PrevScreenOptions.length
    ) {
      return false;
    }

    return true;
  }

  public UNSAFE_componentWillReceiveProps(newProps: Props) {
    const { params, OptionsState, SetOptionsState, readonly } = newProps;

    if (!OptionsState) {
      return;
    }

    //If done move to next screen - Doneness will be checked by the length of the PrevScreenOptions
    if (
      this.props.OptionsState.PrevScreenOptions.length <
      OptionsState.PrevScreenOptions.length
    ) {
      if (params.vrid) {
        Utilities.navigateTo(
          '/restaurant/' +
            params.vrid +
            '/' +
            params.type +
            '/' +
            params.id +
            (params.concessionStoreId ? '/' + params.concessionStoreId : '') +
            '/configure/' +
            params.item +
            '/' +
            OptionsState.Current,
        );
      } else if (readonly) {
        Utilities.navigateTo('/' + params.item + '/' + OptionsState.Current);
      }
    }

    //If come back from another screen revert
    if (
      parseInt(params.index) < parseInt(this.props.params.index) &&
      parseInt(this.props.params.index) === OptionsState.Current
    ) {
      let OptionsBeforePreviousScreen: MenuItemOptionSetItem[] = [];

      if (OptionsState.PrevScreenOptions.length > 2) {
        for (let i = 0; i < OptionsState.PrevScreenOptions.length - 1; i++) {
          OptionsBeforePreviousScreen = OptionsBeforePreviousScreen.concat(
            OptionsState.PrevScreenOptions[i],
          );
        }
      }

      let PrevIndex = 0;
      if (OptionsState.PrevIndices.length > 0) {
        PrevIndex =
          OptionsState.PrevIndices[OptionsState.PrevIndices.length - 1];
      }

      if (OptionsState.Current > 0) {
        SetOptionsState(
          OptionsBeforePreviousScreen,
          OptionsState.PrevScreenOptions.slice(0, -1),
          PrevIndex,
          OptionsState.PrevIndices.slice(0, -1),
        );
      }
    }

    //If illegal replace
    if (parseInt(params.index) > OptionsState.Current) {
      if (params.vrid) {
        hashHistory.replace(
          '/restaurant/' +
            params.vrid +
            '/' +
            params.type +
            '/' +
            params.id +
            (params.concessionStoreId ? '/' + params.concessionStoreId : '') +
            '/configure/' +
            params.item +
            '/' +
            OptionsState.Current,
        );
      } else if (readonly) {
        Utilities.navigateTo('/' + params.item + '/' + OptionsState.Current);
      }
    }
  }

  public componentDidMount() {
    const { readonly } = this.props;

    if (readonly) {
      document.body.scrollTop = document.documentElement.scrollTop = 0;
    }
  }

  public componentDidUpdate(prevProps) {
    const { params, readonly } = this.props;

    if (prevProps.params.index && prevProps.params.index != params.index) {
      if (!readonly) {
        this.scrollOptionsTop();
      }
    }
  }

  private scrollOptionsTop = () => {
    const optionsTopNode = (this as any).optionsTop.current;
    if (optionsTopNode) {
      if (
        optionsTopNode.clientHeight + optionsTopNode.offsetTop >
        window.innerHeight
      ) {
        window.scrollTo(0, optionsTopNode.offsetTop);
      } else {
        window.scrollTo(0, 0);
      }
    }
  };

  private handleOptionComplete = (options: MenuItemOptionSetItem[]) => {
    const { OptionsState, SetOptionsState, Item } = this.props;

    if (Item.status != 'success') {
      return;
    }

    const optionSets = Sorted(Item.data.MenuItemOptionSets);

    // There are 3 possible values here:
    // null        -> skip the subsequent option set
    // -1          -> means this is the last item
    // +ve integer -> skip to the specified option set.
    // Since we have a set of options, and each one has a MenuItemOptionSetItemId
    // field, we need to choose one.
    // 'max' has the following ordering: null < -1 < +ve integer
    const nextOptionSetId: number | undefined = max(
      map(options, (o) => o.NextMenuItemOptionSetId),
    );
    let nextIndex: number;

    switch (nextOptionSetId) {
      case undefined:
      case null:
        // Skip to the next item as normal
        nextIndex = OptionsState.Current + 1;
        break;

      case -1:
        // Skip to the end
        nextIndex = optionSets.length;
        break;

      default:
        const optionSetIds = map(optionSets, (os, idx) => {
          return { id: os.MenuItemOptionSetId, index: idx };
        });

        const optionSetIdToIndex = mapValues(
          keyBy(optionSetIds, 'id'),
          (it) => it.index,
        );

        nextIndex = optionSetIdToIndex[nextOptionSetId];
        break;
    }

    let newOptions: Api.MenuItemOptionSetItem[] = [];

    for (const PrevScreenOption of OptionsState.PrevScreenOptions) {
      newOptions = newOptions.concat(PrevScreenOption);
    }

    newOptions = newOptions.concat(options);

    //Dispatch for the next set of options
    SetOptionsState(
      newOptions,
      OptionsState.PrevScreenOptions.concat([options]),
      nextIndex,
      OptionsState.PrevIndices.concat([OptionsState.Current]),
    );
  };

  //Trigger the option complete if is done
  private handleLiveUpdate = (
    options: MenuItemOptionSetItem[],
    isDone: boolean,
  ) => {
    const { OptionsState, SetLiveOptions } = this.props;

    if (!OptionsState) {
      return;
    }

    const PrevScreenOptions = OptionsState.PrevScreenOptions.slice(-1).pop();

    if (PrevScreenOptions) {
      if (isDone) {
        this.handleOptionComplete(options);
      } else {
        let newOptions: Api.MenuItemOptionSetItem[] = [];

        for (const PrevScreenOption of OptionsState.PrevScreenOptions) {
          newOptions = newOptions.concat(PrevScreenOption);
        }

        newOptions = newOptions.concat(options);

        SetLiveOptions(newOptions);
      }
    }
  };

  private renderSelected = (currency: Api.Currency): JSX.Element => {
    const { AppDesignScheme, isDark, Languages, OptionsState } = this.props;

    const styles = {
      primary: {
        color: AppDesignScheme.Primary,
      },
      secondary: {
        color: AppDesignScheme.Secondary,
      },
      tertiary: {
        color: AppDesignScheme.Tertiary,
      },
      item: {
        borderColor: AppDesignScheme.Secondary,
      },
      darkItem: {
        borderColor: tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString(),
      },
    };

    let initialLang = 'en';

    for (const Language of Languages) {
      if (Language.active) {
        initialLang = Language.code;
        break;
      }
    }

    // TODO: this price function is copied from OptionSet.tsx render
    const price = (o: MenuItemOptionSetItem): JSX.Element | null => {
      if (!o.Price) {
        return null;
      }
      return (
        <p
          className="secondary clear-margins text-right configure__total__content__item__price fd-h4"
          style={isDark ? styles.primary : styles.secondary}
        >
          {ToCurrency(o.Price, currency, initialLang)}
        </p>
      );
    };
    const el = OptionsState.Options.map((o) => {
      let img: JSX.Element | null = null;
      if (o.ImageUrl != null) {
        img = (
          <img
            src={`${o.ImageUrl}?auto=format&w=140&h=120`}
            srcSet={`${o.ImageUrl}?auto=format&w=140&h=120 1x, ${o.ImageUrl}?auto=format&w=140&h=120&dpr=2 2x, ${o.ImageUrl}?auto=format&w=140&h=120&dpr=3 3x`}
            style={isDark ? styles.darkItem : styles.item}
            alt=""
          />
        );
      }

      return (
        <div
          key={o.MenuItemOptionSetItemId}
          className="configure__total__content__item flex flex-align-center"
          style={styles.item}
        >
          {img}
          <p
            className="tertiary clear-margins fd-h4"
            style={isDark ? styles.primary : styles.tertiary}
          >
            {o.Name}
          </p>
          {price(o)}
        </div>
      );
    });
    return <div>{el}</div>;
  };

  public render() {
    const {
      Item,
      AppDesignScheme,
      isDark,
      params,
      Languages,
      Restaurant,
      handleAddItem,
      OptionsState,
      translate,
    } = this.props;
    const { index } = params;

    const styles = {
      nav: {
        ':hover': {},
        ':active': {},
      },
      secondaryActive: {
        color: tinycolor(AppDesignScheme.Secondary)
          .darken(10)
          .toString(),
      },
      secondaryHover: {
        color: tinycolor(AppDesignScheme.Secondary)
          .darken(5)
          .toString(),
      },
      primary: {
        color: AppDesignScheme.Primary,
      },
      secondary: {
        color: AppDesignScheme.Secondary,
      },
      tertiary: {
        color: AppDesignScheme.Tertiary,
      },
      header: {
        background: tinycolor(AppDesignScheme.Primary)
          .darken(3)
          .toString(),
        boxShadow: `1px 1px 9px -5px ${AppDesignScheme.Secondary}`,
        WebkitBoxShadow: `1px 1px 9px -5px ${AppDesignScheme.Secondary}`,
        borderColor: tinycolor(AppDesignScheme.Primary)
          .darken(20)
          .toString(),
      },
      darkHeader: {
        background: tinycolor(AppDesignScheme.Tertiary)
          .lighten(10)
          .toString(),
        borderColor: tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString(),
        boxShadow: `1px 1px 9px -5px ${tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString()}`,
        WebkitBoxShadow: `1px 1px 9px -5px ${tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString()}`,
      },
      price: {
        borderColor: tinycolor(AppDesignScheme.Primary)
          .darken(20)
          .toString(),
      },
      totalHeader: {
        background: AppDesignScheme.Secondary,
      },
      total: {
        background: tinycolor(AppDesignScheme.Primary)
          .darken(3)
          .toString(),
        WebkitBoxShadow: `1px 1px 9px -5px ${AppDesignScheme.Secondary}`,
        boxShadow: `1px 1px 9px -5px ${AppDesignScheme.Secondary}`,
        borderColor: tinycolor(AppDesignScheme.Primary)
          .darken(20)
          .toString(),
      },
      darkTotal: {
        background: tinycolor(AppDesignScheme.Tertiary)
          .lighten(10)
          .toString(),
        WebkitBoxShadow: `1px 1px 9px -5px ${tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString()}`,
        boxShadow: `1px 1px 9px -5px ${tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString()}`,
        borderColor: tinycolor(AppDesignScheme.Tertiary)
          .darken(10)
          .toString(),
      },
      buttonSecondary: {
        backgroundColor: AppDesignScheme.Secondary,
        ':hover': {},
        ':active': {},
        ':focus': {},
      },
      buttonActive: {
        backgroundColor: tinycolor(AppDesignScheme.Secondary)
          .darken(10)
          .toString(),
      },
      buttonHover: {
        backgroundColor: tinycolor(AppDesignScheme.Secondary)
          .darken(5)
          .toString(),
      },
      hr: {
        borderColor: AppDesignScheme.Secondary,
      },
    };

    let initialLang = 'en';
    for (const Language of Languages) {
      if (Language.active) {
        initialLang = Language.code;
        break;
      }
    }

    if (Item.status != 'success') {
      return <LoadingPanel AppDesignScheme={AppDesignScheme} isDark={isDark} />;
    }

    const currency = Restaurant ? Routes.Currency(Restaurant) : 1;

    const optionSets = Sorted(Item.data.MenuItemOptionSets);

    // This code is relying on the fact that we get null
    // if we index off the end, rather than doing an index check.

    //Sort by DisplayOrder
    const options: JSX.Element[] = optionSets
      .sort((os1, os2) => os1.DisplayOrder - os2.DisplayOrder)
      .map((os, idx) => {
        return (
          <OptionSet
            key={idx}
            Currency={currency}
            Show={idx === parseInt(index)}
            Item={os}
            ParentItem={Item}
            Current={parseInt(index)}
            AddItem={handleAddItem}
            translate={translate}
            Languages={Languages}
            AppDesignScheme={AppDesignScheme}
            isDark={isDark}
            LiveUpdate={this.handleLiveUpdate}
          />
        );
      });

    const allDone = parseInt(index) === optionSets.length;

    let doneButton: JSX.Element | null = null;

    if (allDone) {
      doneButton = (
        <button
          className="fd-button fd-button--big fd-button--secondary hidden-sm hidden-xs"
          onClick={handleAddItem(Item.data)}
          key="done-button"
          style={
            [
              styles.buttonSecondary,
              Radium.getState(this.state, 'done-button', ':active')
                ? styles.buttonActive
                : Radium.getState(this.state, 'done-button', ':hover')
                ? styles.buttonHover
                : null,
            ] as any
          }
        >
          <p className="clear-margins primary" style={styles.primary}>
            {translate('Add_to_basket')}
          </p>
        </button>
      );
    }

    const item = {
      Item: Item.data,
      Options: OptionsState.Options,
    };
    const totalConfigured = B.ItemTotal(item);

    const price =
      Item.data.Price > 0 ? (
        <Price
          className="hidden-sm hidden-xs"
          Price={Item.data.Price}
          Currency={currency}
          AppDesignScheme={AppDesignScheme}
          isDark={isDark}
          Languages={Languages}
        />
      ) : null;

    const responsivePrice =
      Item.data.Price > 0 ? (
        <div>
          <hr className="visible-sm-block visible-xs-block" style={styles.hr} />
          <p
            className="secondary clear-margins bold visible-sm-block visible-xs-block fd-h4"
            style={styles.secondary}
          >
            {ToCurrency(Item.data.Price, currency, initialLang)}
          </p>
        </div>
      ) : null;

    let description: JSX.Element | null = null;

    if (Item.data.ImageUrl == null) {
      description = (
        <div
          className="configure__header flex"
          style={isDark ? styles.darkHeader : styles.header}
        >
          <div className="configure__header__details flex-one">
            <p
              className="secondary clear-margins fd-h3"
              style={isDark ? styles.primary : styles.secondary}
            >
              {Item.data.Name}
            </p>

            <p
              className="tertiary clear-margins fd-h5"
              style={isDark ? styles.primary : styles.tertiary}
            >
              {Item.data.Description}
            </p>

            {responsivePrice}
          </div>
          {price}
        </div>
      );
    } else {
      description = (
        <div
          className="configure__header configure__header--img flex"
          style={isDark ? styles.darkHeader : styles.header}
        >
          <img
            src={`${Item.data.ImageUrl}?auto=format&w=200&h=120`}
            srcSet={`${Item.data.ImageUrl}?auto=format&w=200&h=120 1x, ${Item.data.ImageUrl}?auto=format&w=200&h=120&dpr=2 2x, ${Item.data.ImageUrl}?auto=format&w=200&h=120&dpr=3 3x`}
            alt=""
          />
          <div className="configure__header__details flex-one">
            <p
              className="clear-margins secondary fd-h3"
              style={isDark ? styles.primary : styles.secondary}
            >
              {Item.data.Name}
            </p>

            <p
              className="tertiary clear-margins fd-h5"
              style={isDark ? styles.primary : styles.tertiary}
            >
              {Item.data.Description}
            </p>

            {responsivePrice}
          </div>
          {price}
        </div>
      );
    }

    let hideOptions = true;

    for (const option of options) {
      hideOptions = hideOptions && !option.props.Show;
    }

    return (
      <React.Fragment>
        {description}

        <div
          className="configure__total"
          style={isDark ? styles.darkTotal : styles.total}
        >
          <div
            className="configure__total__header flex flex-align-center flex-justify-between"
            style={styles.totalHeader}
          >
            <h2 className="primary clear-margins fd-h3" style={styles.primary}>
              {translate('Total')}
            </h2>
            <p className="primary clear-margins fd-h3" style={styles.primary}>
              {ToCurrency(totalConfigured, currency, initialLang)}
            </p>
          </div>
          <div className="configure__total__content">
            {this.renderSelected(currency)}
          </div>
        </div>

        <div
          className={`configure__options ${
            options === null || hideOptions ? 'configure__options--null' : ''
          }`}
          ref={(this as any).optionsTop}
        >
          {options}
        </div>

        <div className="text-center configure__buttons">{doneButton}</div>
      </React.Fragment>
    );
  }
}

export { ConfigureItem };
export default Radium(ConfigureItem);
